import React, { useState, useEffect, useRef } from 'react';
import { Chip } from '@material-ui/core';
import './ComposerChip.scss';

/**
 * ComposerChip - A chip component for composers with an inline percentage split input
 *
 * @param {object} composer - The composer object with name/label and optional split
 * @param {number} index - The index in the array (for getTagProps)
 * @param {function} getTagProps - Function from Autocomplete to get tag props (includes onDelete)
 * @param {function} onSplitChange - Callback when split percentage changes: (composerId, newSplit) => void
 * @param {number} placeholderValue - Pre-calculated placeholder value for this composer
 */
const ComposerChip = ({
  composer,
  index,
  getTagProps,
  onSplitChange,
  placeholderValue = 100
}) => {
  // Extract only the props we need from tagProps, avoiding event handlers that interfere
  const tagProps = getTagProps({ index });
  const { onDelete, key } = tagProps;

  const composerName = composer.name || composer.label || composer.value || composer.id || (typeof composer === 'string' ? composer : '');
  const composerSplit = composer.split !== undefined ? composer.split : '';

  // Use local state to manage the input value during typing
  const [localSplit, setLocalSplit] = useState(composerSplit);
  const isUserEditing = useRef(false);

  // Sync local state when composer.split changes from outside (e.g., loading data)
  // But not when user is actively editing
  useEffect(() => {
    if (!isUserEditing.current) {
      setLocalSplit(composer.split !== undefined ? composer.split : '');
    }
  }, [composer.split]);

  // Use the pre-calculated placeholder value passed from parent
  const smartPlaceholder = placeholderValue;

  const handleFocus = (e) => {
    e.stopPropagation();
    isUserEditing.current = true;
  };

  const handleSplitChange = (e) => {
    e.stopPropagation();
    const value = e.target.value;

    // Limit to 1 decimal place
    if (value === '' || value === '.') {
      setLocalSplit(value);
      return;
    }

    // Check if value has more than 1 decimal place
    const parts = value.split('.');
    if (parts.length > 1 && parts[1].length > 1) {
      // Truncate to 1 decimal place
      setLocalSplit(`${parts[0]}.${parts[1].charAt(0)}`);
      return;
    }

    // Update local state immediately for responsive typing
    setLocalSplit(value);
  };

  const handleBlur = (e) => {
    e.stopPropagation();

    // Get composer ID for reliable state updates
    const composerId = composer.id || composer.value;

    // On blur, validate and commit the value to parent state
    const value = localSplit;
    if (value === '') {
      onSplitChange(composerId, '');
    } else {
      const numValue = Number(value);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
        onSplitChange(composerId, numValue);
      } else {
        // Reset to previous valid value if invalid
        setLocalSplit(composerSplit);
      }
    }

    // Set editing flag to false AFTER committing
    // Use setTimeout to allow state update to complete first
    setTimeout(() => {
      isUserEditing.current = false;
    }, 0);
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="composer-chip-wrapper">
      <Chip
        key={key}
        onDelete={onDelete}
        label={
          <span className="composer-chip-content">
            <span className="composer-chip-name">{composerName}</span>
            <input
              type="number"
              className="composer-chip-split-input"
              value={localSplit}
              onChange={handleSplitChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onKeyDown={stopPropagation}
              onKeyUp={stopPropagation}
              onKeyPress={stopPropagation}
              onClick={stopPropagation}
              onMouseDown={stopPropagation}
              placeholder={`${smartPlaceholder}`}
              min="0"
              max="100"
              title="Split percentage"
            />
            <span className="composer-chip-percent">%</span>
          </span>
        }
        className="composer-chip"
      />
    </div>
  );
};

export default ComposerChip;
